<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isValid from 'date-fns/isValid';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import BuzzMentionSourceCell from './BuzzMentionSourceCell.vue';
import BuzzMentionDomainCell from './BuzzMentionDomainCell.vue';
import { mentionHost } from './buzzMentionHelpers';

const props = defineProps({
  mentions: {
    type: Array,
    default: () => [],
  },
  emptyLabelKey: {
    type: String,
    default: 'BUZZ_REPORTS.NO_MENTIONS',
  },
});

const { t } = useI18n();

const sourceLabel = mention => {
  const source = (mention.source || 'google').toLowerCase();
  if (source === 'facebook') return t('BUZZ_REPORTS.SOURCE_FACEBOOK');
  if (source === 'instagram' || mention.kind === 'instagram') {
    return t('BUZZ_REPORTS.SOURCE_INSTAGRAM');
  }
  if (source === 'tiktok' || mention.kind === 'tiktok') {
    return t('BUZZ_REPORTS.SOURCE_TIKTOK');
  }
  if (source === 'linkedin' || mention.kind === 'linkedin') {
    return t('BUZZ_REPORTS.SOURCE_LINKEDIN');
  }
  if (mention.kind === 'news' || mention.source === 'news') {
    return t('BUZZ_REPORTS.SOURCE_GOOGLE_NEWS');
  }
  if (mention.kind === 'web') return t('BUZZ_REPORTS.SOURCE_GOOGLE');
  return t('BUZZ_REPORTS.SOURCE_GOOGLE');
};

const mentionDate = mention => {
  if (mention.date) return mention.date;
  if (!mention.crawled_at) return '—';
  const parsed = parseISO(mention.crawled_at);
  if (!isValid(parsed)) return '—';
  return format(parsed, 'MMM d, yyyy');
};

const tableData = computed(() =>
  props.mentions.map((mention, index) => {
    const domain = mentionHost(mention.domain, mention.link);
    return {
      id: `${mention.link || mention.title}-${index}`,
      sourceLabel: sourceLabel(mention),
      sourceKey: mention.source || 'google',
      title: mention.title || mention.link || '—',
      link: mention.link,
      date: mentionDate(mention),
      domain: domain || '—',
    };
  })
);

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('sourceLabel', {
    header: t('BUZZ_REPORTS.TABLE.SOURCE'),
    size: 72,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(BuzzMentionSourceCell, {
        source: row.sourceKey,
        label: row.sourceLabel,
      });
    },
  }),
  columnHelper.accessor('title', {
    header: t('BUZZ_REPORTS.TABLE.TITLE'),
    size: 420,
    cell: cellProps => {
      const row = cellProps.row.original;
      if (row.link) {
        return h(
          'a',
          {
            href: row.link,
            target: '_blank',
            rel: 'noopener noreferrer',
            class: 'text-n-brand hover:underline',
          },
          row.title
        );
      }
      return h('span', { class: 'text-n-slate-12' }, row.title);
    },
  }),
  columnHelper.accessor('date', {
    header: t('BUZZ_REPORTS.TABLE.DATE'),
    size: 140,
    cell: cellProps =>
      h(
        'span',
        { class: 'text-n-slate-11 whitespace-nowrap' },
        cellProps.getValue()
      ),
  }),
  columnHelper.accessor('domain', {
    header: t('BUZZ_REPORTS.TABLE.DOMAIN'),
    size: 180,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(BuzzMentionDomainCell, {
        domain: row.domain,
        link: row.link,
      });
    },
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: 10,
    },
  },
});
</script>

<template>
  <p v-if="!mentions.length" class="text-sm text-n-slate-10 mb-0">
    {{ t(emptyLabelKey) }}
  </p>
  <div v-else class="overflow-x-auto">
    <Table :table="table" />
    <Pagination class="px-5 py-3 print:hidden" :table="table" />
  </div>
</template>
